import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import axios from 'axios';
import { normalizeImageUrl } from '../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_COLORS = {
  announcement: 'bg-[#4ECDC4] text-white',
  event: 'bg-[#D4A84B] text-white',
  notice: 'bg-[#9ACD32] text-white',
};

const formatDate = (s) => {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function NewsDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    axios.get(`${API}/news/${id}`)
      .then(r => { if (alive) setArticle(r.data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="container-custom py-16 max-w-3xl">
        <Skeleton className="h-8 w-32 mb-6" />
        <Skeleton className="h-10 w-3/4 mb-4" />
        <Skeleton className="h-64 w-full mb-6 rounded-xl" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="container-custom py-24 text-center" data-testid="news-not-found">
        <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-3">Article not found</h2>
        <p className="text-gray-500 mb-6">The article you're looking for may have been removed or is no longer available.</p>
        <Link to="/news"><Button className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-6"><ArrowLeft size={16} className="mr-2" />Back to News</Button></Link>
      </div>
    );
  }

  const img = normalizeImageUrl(article.image_url);

  return (
    <div className="page-transition" data-testid="news-detail">
      <article className="container-custom py-12 md:py-16 max-w-3xl">
        <Link to="/news" className="inline-flex items-center text-sm text-[#4ECDC4] hover:underline mb-6">
          <ArrowLeft size={14} className="mr-1" />Back to News
        </Link>

        <div className="flex items-center gap-3 mb-4">
          {article.category && (
            <Badge className={`${CATEGORY_COLORS[article.category] || 'bg-gray-500 text-white'} capitalize`}>
              <Tag size={12} className="mr-1" />{article.category}
            </Badge>
          )}
          <span className="flex items-center gap-1 text-sm text-gray-500">
            <Calendar size={14} />{formatDate(article.created_at)}
          </span>
        </div>

        <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6 leading-tight" data-testid="news-title">
          {article.title}
        </h1>

        {img && (
          <img src={img} alt={article.title} className="w-full h-auto rounded-2xl shadow-md mb-8 object-cover" />
        )}

        <Card className="border-0 shadow-none">
          <CardContent className="px-0">
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap" data-testid="news-content">
              {article.content}
            </div>
          </CardContent>
        </Card>

        <div className="mt-12 pt-6 border-t border-gray-100">
          <Link to="/news"><Button variant="outline" className="rounded-full"><ArrowLeft size={14} className="mr-1" />All News</Button></Link>
        </div>
      </article>
    </div>
  );
}
